import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { TRANSLATIONS } from "@/i18n/translations";
import { SUPPORTED_LANGUAGES } from "@/i18n/languages";
import { PATH_LANG, cleanUrl, localizedUrl, urlLang } from "@/i18n/locale";

const LanguageContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// hreflang per locale — must match HREFLANG_BY_LANG in backend/server.py for sitemap parity.
export const HREFLANG_BY_LANG = {
  en: "en", nl: "nl-NL", de: "de-DE", fr: "fr-FR", es: "es", it: "it-IT",
  pt: "pt", pl: "pl-PL", sv: "sv-SE", da: "da-DK", fi: "fi-FI", no: "no",
  el: "el-GR", cs: "cs-CZ", ro: "ro-RO", hu: "hu-HU", sk: "sk-SK", bg: "bg-BG",
  hr: "hr-HR", sl: "sl-SI", et: "et-EE", lv: "lv-LV", lt: "lt-LT",
};

// Inject hreflang + canonical link tags into <head> for the current path.
// Canonical is SELF-REFERENCING per language variant (path-based: /de/apps/x),
// so every localized URL is independently indexable.
const upsertHeadLinks = () => {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  const clean = cleanUrl(window.location.href);
  // Remove our own previous links (data-hreflang attribute identifies them)
  Array.from(document.head.querySelectorAll("link[data-hreflang]")).forEach((el) => el.remove());

  const make = (rel, href, hreflang) => {
    const link = document.createElement("link");
    link.setAttribute("rel", rel);
    link.setAttribute("href", href);
    if (hreflang) link.setAttribute("hreflang", hreflang);
    link.setAttribute("data-hreflang", "1");
    document.head.appendChild(link);
  };

  // Canonical → self-referencing localized URL (derived from the URL, not from
  // localStorage, so prerendered snapshots are deterministic).
  let canon = document.head.querySelector('link[rel="canonical"]');
  if (!canon) {
    canon = document.createElement("link");
    canon.setAttribute("rel", "canonical");
    document.head.appendChild(canon);
  }
  canon.setAttribute("href", localizedUrl(clean, urlLang()));

  // Alternate links for every supported language (path-based URLs)
  Object.entries(HREFLANG_BY_LANG).forEach(([code, hreflang]) => {
    make("alternate", localizedUrl(clean, code), hreflang);
  });
  make("alternate", clean, "x-default");
};

// Tiny stable hash for the source bundle (matches md5 enough server-side via
// length+chars — backend recomputes md5 itself; we just send something stable).
const stableStringify = (obj) => {
  const keys = (o) => (typeof o === "object" && o !== null && !Array.isArray(o)
    ? Object.keys(o).sort().reduce((acc, k) => ({ ...acc, [k]: keys(o[k]) }), {})
    : o);
  return JSON.stringify(keys(obj));
};

const computeHash = async (str) => {
  if (typeof crypto !== "undefined" && crypto.subtle) {
    const buf = await crypto.subtle.digest("MD5", new TextEncoder().encode(str)).catch(() => null);
    if (buf) return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");
  }
  // Fallback: simple hash (still stable per browser)
  let h = 0;
  for (let i = 0; i < str.length; i++) h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  return `js-${h}`;
};

const CACHE = { en: TRANSLATIONS.en, nl: TRANSLATIONS.nl };

// Exposed helpers so admin tools can re-use the exact same source bundle + hash as the runtime
// translator (otherwise the cache won't match).
export const EN_BUNDLE = TRANSLATIONS.en;
export const stableStringifyBundle = stableStringify;
export const computeBundleHash = computeHash;

export const LanguageProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    if (typeof window === "undefined") return "en";
    // 1) Path prefix /de/... wins (path-based locale URLs)
    if (PATH_LANG) return PATH_LANG;
    // 2) Legacy URL ?lang=xx (old sitemap format — locale.js redirects these)
    const url = new URL(window.location.href);
    const fromUrl = url.searchParams.get("lang");
    if (fromUrl && SUPPORTED_LANGUAGES.some((l) => l.code === fromUrl)) return fromUrl;
    // 3) localStorage
    const stored = window.localStorage.getItem("hac_lang");
    return SUPPORTED_LANGUAGES.some((l) => l.code === stored) ? stored : "en";
  });
  const [bundle, setBundle] = useState(CACHE[lang] || TRANSLATIONS.en);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("hac_lang", lang);
      document.documentElement.lang = lang;
    }
  }, [lang]);

  // Re-inject hreflang + canonical on every route change (location.pathname is captured via popstate / pushstate)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const refresh = () => upsertHeadLinks();
    refresh();
    window.addEventListener("popstate", refresh);
    // Patch history.pushState / replaceState to also fire so SPA navigations refresh hreflang
    const wrap = (fn) => function (...args) {
      const ret = fn.apply(this, args);
      refresh();
      return ret;
    };
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = wrap(origPush);
    window.history.replaceState = wrap(origReplace);
    return () => {
      window.removeEventListener("popstate", refresh);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (CACHE[lang]) {
        setBundle(CACHE[lang]);
        return;
      }
      setLoading(true);
      try {
        const source = TRANSLATIONS.en;
        const hash = await computeHash(stableStringify(source));
        const { data } = await axios.post(
          `${API}/i18n/translate`,
          { lang, source, source_hash: hash },
          { timeout: 60000 },
        );
        if (cancelled) return;
        CACHE[lang] = data;
        setBundle(data);
      } catch (e) {
        if (!cancelled) setBundle(TRANSLATIONS.en);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [lang]);

  const toggle = () => setLang((l) => (l === "en" ? "nl" : "en"));

  // changeLang — user-initiated language switch: navigate to the path-based
  // localized URL (full load) so URL, canonical and content always agree.
  const changeLang = (code) => {
    if (!SUPPORTED_LANGUAGES.some((l) => l.code === code)) return;
    window.localStorage.setItem("hac_lang", code);
    const target = localizedUrl(cleanUrl(window.location.href), code);
    if (target !== window.location.href) {
      window.location.assign(target);
    } else {
      setLang(code);
    }
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, changeLang, toggle, t: bundle, loading }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLang = () => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be used inside LanguageProvider");
  return ctx;
};
